/**
 * Every failure the UI sees has this shape. `code` is the backend's machine
 * code and doubles as the i18n key, which is how `booking.slot_is_full`
 * becomes a Persian sentence with a "join the waitlist" action
 * (CLAUDE.md §5.6, §7.4).
 *
 * It extends Error on purpose: TanStack Query, error boundaries and stack
 * traces all expect a real Error instance.
 */
export class AppError extends Error {
  public readonly code: string;

  public readonly status: number;

  public readonly fields: Readonly<Record<string, readonly string[]>> | undefined;

  public readonly context: Readonly<Record<string, unknown>> | undefined;

  public readonly correlationId: string | undefined;

  public constructor(init: {
    code: string;
    status: number;
    message?: string;
    fields?: Readonly<Record<string, readonly string[]>>;
    context?: Readonly<Record<string, unknown>>;
    correlationId?: string;
    /** kept for the console and error reporting only — never rendered */
    cause?: unknown;
  }) {
    // the human-readable text is resolved from `code` through i18n, never here
    super(init.message ?? init.code, init.cause === undefined ? undefined : { cause: init.cause });

    this.name = 'AppError';
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields;
    this.context = init.context;
    this.correlationId = init.correlationId;
  }
}

export const UNKNOWN_ERROR_CODE = 'common.server_error';

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

export function createAppError(
  partial: Partial<ConstructorParameters<typeof AppError>[0]> & { status: number },
): AppError {
  return new AppError({ ...partial, code: partial.code ?? UNKNOWN_ERROR_CODE });
}
